import React, { useState } from "react";
import styles from "./AdminList.module.css";
import IconButton from "./IconButton"; // IconButton 컴포넌트 import
import { ICON_PATHS } from "../../constants/iconPaths";

function AdminList({ headers, data, onRowClick }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" }); // 정렬 상태 관리
  const [searchKeyword, setSearchKeyword] = useState(""); // 검색 키워드 상태

  // 검색 및 정렬 데이터 계산
  const filteredAndSortedData = React.useMemo(() => {
    // 검색 필터링
    const filtered = data.filter((row) =>
      headers.some(({ key }) => 
        (row[key] || "").toString().toLowerCase().includes(searchKeyword.toLowerCase())
      )
    );

    // 정렬
    if (!sortConfig.key) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.key] || ""; // 데이터가 없으면 빈 문자열로 처리
      const bValue = b[sortConfig.key] || ""; // 데이터가 없으면 빈 문자열로 처리

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, sortConfig, searchKeyword, headers]);

  // 정렬 상태 토글 함수
  const handleHeaderClick = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" }; // 기본값: 오름차순
    });
  };

  // 검색 키워드 입력 핸들러
  const handleSearchChange = (event) => {
    setSearchKeyword(event.target.value);
  };

  // 프리뷰 링크 복사 함수
  const copyPreviewLink = (id, team) => {
    const previewUrl = `${window.location.origin}/project/ptl/preview/${id}?team=${team}`;
    navigator.clipboard.writeText(previewUrl)
      .then(() => {
        alert("프리뷰 링크가 복사되었습니다.");
      })
      .catch((err) => {
        console.error("링크 복사 실패:", err);
        alert("링크 복사에 실패했습니다.");
      });
  };

  // 프리뷰 열기 함수
  const openPreview = (id, team) => {
    window.open(`/project/ptl/preview/${id}?team=${team}`, "_blank");
  };

  // 팀 셀 렌더링 함수
  const renderTeamCell = (row, key) => {
    const teamName = row[key]?.name || row[key] || "-";
    const teamKey = key; // team1, team2, team3

    return (
      <div className={styles.teamCell}>
        <span>{teamName}</span>
        {teamName !== "-" && (
          <div className={styles.teamCellActions}>
            <IconButton
              icon={ICON_PATHS.externalLink} // 프리뷰 아이콘 파일명
              onClick={(e) => {
                e.stopPropagation();
                openPreview(row.id, teamKey);
              }}
              size="small"
            />
            <IconButton
              icon={ICON_PATHS.copy} // 복사 아이콘 파일명
              onClick={(e) => {
                e.stopPropagation();
                copyPreviewLink(row.id, teamKey);
              }}
              size="small"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={`${styles.row} ${styles.headerRow}`}>
        {headers.map(({ label, key }, index) => (
          <div
            key={index}
            className={`${styles.cell} ${styles.headerCell}`}
            onClick={() => handleHeaderClick(key)} // 클릭 시 정렬
            style={{ cursor: "pointer" }}
          >
            {label}
            {sortConfig.key === key && (
              <span>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Search Bar */}
      <div className={styles.searchContainer}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="검색어를 입력하세요..."
          value={searchKeyword}
          onChange={handleSearchChange}
        />
      </div>

      {/* Data Rows */}
      {filteredAndSortedData.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={`${styles.row} ${styles.dataRow}`}
          onClick={() => onRowClick(row)}
          style={{ cursor: "pointer" }}
        >
          {headers.map(({ key, renderKey }, colIndex) => (
            <div key={colIndex} className={`${styles.cell} ${styles.dataCell}`}>
              {key.startsWith('team') ? renderTeamCell(row, key) : 
                // renderKey가 있고 해당 값이 React 엘리먼트인 경우 그대로 렌더링
                (renderKey && React.isValidElement(row[renderKey])) ? 
                  row[renderKey] : 
                  (row[key] || "-")}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default AdminList;